import type {FullInfo} from '@rocket-ui/lk-api';
import {useFetchProfile} from '@rocket-ui/lk-api';
import {useRegistrationCountries} from 'features/registration/model';
import {
	formatProfileGender,
	formatProfilePhone,
	formatProfileText,
	getCitizenshipOksmCode,
	getResidenceOksmCode,
	isRussiaCitizenship,
} from './formatProfile';
import {isIdentityVerificationPending, isIdentityVerificationRejected} from './identityVerification';

export type ProfileViewModel = {
	citizenshipOksmCode: string | null;
	citizenshipName: string;
	residenceOksmCode: string | null;
	residenceName: string;
	isRussiaCitizenship: boolean;
	isRussiaResidence: boolean;
	gender: string;
	secondName: string;
	firstName: string;
	patronymic: string;
	phone: string;
	email: string;
};

function formatCountryName(
	oksmCode: string | null,
	getCountryName: (oksmCode: string | null) => string | undefined,
): string {
	const countryName = getCountryName(oksmCode)?.trim();

	return countryName || (oksmCode ? oksmCode : '—');
}

const EMPTY_PROFILE: ProfileViewModel = {
	citizenshipOksmCode: null,
	citizenshipName: '—',
	residenceOksmCode: null,
	residenceName: '—',
	isRussiaCitizenship: false,
	isRussiaResidence: false,
	gender: '—',
	secondName: '—',
	firstName: '—',
	patronymic: '—',
	phone: '—',
	email: '—',
};

export function mapFullInfoToProfileView(
	fullInfo: FullInfo | undefined,
	getCountryName: (oksmCode: string | null) => string | undefined,
): ProfileViewModel {
	if (!fullInfo) {
		return EMPTY_PROFILE;
	}

	const info = fullInfo.info;
	const citizenshipOksmCode = getCitizenshipOksmCode(fullInfo);
	const residenceOksmCode = getResidenceOksmCode(fullInfo);

	return {
		citizenshipOksmCode,
		citizenshipName: formatCountryName(citizenshipOksmCode, getCountryName),
		residenceOksmCode,
		residenceName: formatCountryName(residenceOksmCode, getCountryName),
		isRussiaCitizenship: isRussiaCitizenship(citizenshipOksmCode),
		isRussiaResidence: isRussiaCitizenship(residenceOksmCode),
		gender: formatProfileGender(info?.gender),
		secondName: formatProfileText(info?.secondName),
		firstName: formatProfileText(info?.firstName),
		patronymic: formatProfileText(info?.patronymic),
		phone: formatProfilePhone(info?.phone),
		email: formatProfileText(info?.email),
	};
}

/** GET `/executor_web/profile` → данные для экрана «Данные». */
export function useProfile() {
	const query = useFetchProfile();
	const citizenshipOksmCode = getCitizenshipOksmCode(query.data);
	const residenceOksmCode = getResidenceOksmCode(query.data);
	const {getCountryByCode} = useRegistrationCountries(undefined, {
		enabled: Boolean(citizenshipOksmCode) || Boolean(residenceOksmCode) || query.isSuccess,
	});

	const profile = mapFullInfoToProfileView(query.data, (code) =>
		code ? getCountryByCode(code)?.name : undefined,
	);

	return {
		profile,
		fullInfo: query.data,
		isIdentityVerificationPending: isIdentityVerificationPending(query.data),
		isIdentityVerificationRejected: isIdentityVerificationRejected(query.data),
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		isSuccess: query.isSuccess,
		refetch: query.refetch,
	};
}
